import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Table,
  Card,
  Statistic,
  Tag,
  Typography,
  Row,
  Col,
  Progress,
  Button,
  Input,
  Select,
  Space,
  Tooltip,
  Empty,
  message,
  DatePicker,
  Modal,
  Form,
} from "antd";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { LineWave } from "react-loader-spinner";
import {
  DollarCircleOutlined,
  ProfileOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  AlertOutlined,
  UserOutlined,
  SearchOutlined,
  DownloadOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  FallOutlined,
  ClearOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Zambian theme colors
const zambianColors = {
  green: "#4d8c4a",
  red: "#d16b6b",
  orange: "#e99e54",
  black: "#444444",
  white: "#FFFFFF",
  lightGreen: "#80b782",
  lightOrange: "#f4c28e",
};

const LoansDisbursedPage = () => {
  const { company, user } = useContext(AppContext);
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedLoanForReassign, setSelectedLoanForReassign] = useState(null);
  const [reassignForm] = Form.useForm();

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const statusMap = {
    7: {
      text: "Active",
      color: "green",
      icon: <SyncOutlined spin />,
    },
    1: {
      text: "Pending Approval",
      color: "orange",
      icon: <ProfileOutlined />,
    },
    3: {
      text: "Closed",
      color: zambianColors.black,
      icon: <CheckCircleOutlined />,
    },
    8: { text: "Overdue", color: zambianColors.red, icon: <AlertOutlined /> },
    2: {
      text: "New Application",
      color: zambianColors.lightGreen,
      icon: <UserOutlined />,
    },
    9: {
      text: "Defaulted",
      color: zambianColors.red,
      icon: <FallOutlined />,
    },
    10: {
      text: "🎉 Settled",
      color: "green",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
    },
    4: {
      text: "Rejected",
      color: "red",
      icon: <CloseCircleOutlined />,
    },
    12: {
      text: "App Application",
      color: "purple",
      icon: <ProfileOutlined />,
    },
  };

  // Fetch all loans and users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [loansResponse, usersResponse] = await Promise.all([
          axios.get(
            `https://zedlenders.pickmesms.com/items/loans?fields=*,collateral.id,borrower.id,borrower.first_name,borrower.last_name,loan_product.loan_name,loan_product.*,gps_trackers.id,gps_trackers.gps_code,gps_trackers.status,assigned_to.*&filter[company]=${company?.id}&sort=-id&limit=-1`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_neq]=Borrower&filter[company][_eq]=${company?.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
        ]);

        // console.log("Fetched loans:", loansResponse.data.data);
        // console.log("Fetched users:", usersResponse.data.data);
        // console.log("Current user:", user);

        setLoans(loansResponse.data.data);
        setUsers(usersResponse.data.data);

        // Do not pre-filter by officer; show all loans by default
      } catch (err) {
        setError(err);
        message.error("Failed to load loans data");
      } finally {
        setLoading(false);
      }
    };

    if (company?.id && token && user?.user_id) {
      fetchData();
    }
  }, [company?.id, token, user?.user_id]);

  // Apply filters
  useEffect(() => {
    let filtered = [...loans];

    // Filter by search text (borrower name or loan ID)
    if (searchText) {
      filtered = filtered.filter((loan) => {
        const borrowerName = loan.borrower
          ? `${loan.borrower.first_name} ${loan.borrower.last_name}`.toLowerCase()
          : "";
        const loanId = String(loan.id).toLowerCase();
        const searchLower = searchText.toLowerCase();
        return (
          borrowerName.includes(searchLower) || loanId.includes(searchLower)
        );
      });
    }

    // Filter by loan officer
    if (selectedOfficer) {
      filtered = filtered.filter((loan) => {
        if (selectedOfficer === "unassigned") {
          return !loan.assigned_to || !loan.assigned_to.id;
        }
        return loan.assigned_to?.id === selectedOfficer;
      });
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((loan) => loan.loan_status === selectedStatus);
    }

    // Filter by next payment date range
    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter((loan) => {
        if (!loan.next_payment_date) return false;
        const paymentDate = moment(loan.next_payment_date);
        return paymentDate.isBetween(dateRange[0], dateRange[1], "day", "[]");
      });
    }

    setFilteredLoans(filtered);
  }, [loans, searchText, selectedOfficer, selectedStatus, dateRange]);

  const clearFilters = () => {
    setSearchText("");
    setSelectedOfficer(null);
    setSelectedStatus(null);
    setDateRange(null);
  };

  const handleViewDetails = (record) => {
    navigate(`/loan/${record.id}`);
  };

  const handleReassignLoan = (record) => {
    setSelectedLoanForReassign(record);
    setShowReassignModal(true);
  };

  const handleReassignSubmit = async (values) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${selectedLoanForReassign.id}`,
        {
          assigned_to: values.assigned_to,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      message.success("Loan reassigned successfully");
      setShowReassignModal(false);
      reassignForm.resetFields();

      // Refresh the loans data
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?fields=*,collateral.id,borrower.id,borrower.first_name,borrower.last_name,loan_product.loan_name,loan_product.*,gps_trackers.id,gps_trackers.gps_code,gps_trackers.status,assigned_to.*&filter[company]=${company?.id}&sort=-id&limit=-1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLoans(response.data.data);
    } catch (error) {
      console.error("Error reassigning loan:", error);
      message.error("Failed to reassign loan");
    }
  };

  const handleExportToExcel = () => {
    if (!filteredLoans || filteredLoans.length === 0) {
      message.warning("No loans to export");
      return;
    }

    try {
      const exportData = filteredLoans.map((loan, index) => ({
        "S/N": index + 1,

        "Borrower Name": loan.borrower
          ? `${loan.borrower.first_name} ${loan.borrower.last_name}`
          : "No borrower assigned",
        "Loan Product": loan.loan_product?.loan_name || "Unknown",
        "Amount (K)": loan.amount,
        Status: statusMap[loan.loan_status]?.text || "Unknown",
        "Loan Officer": loan.assigned_to
          ? `${loan.assigned_to.first_name} ${loan.assigned_to.last_name}`
          : "Unassigned",
        "Officer Role": loan.assigned_to?.role?.name || "N/A",
        "Application Date": loan.application_date || "Not recorded",
        "Approval Date": loan.approval_date || "Not approved",
        "Next Payment Date":
          loan.loan_status === 10
            ? "N/A - Loan Settled"
            : loan.next_payment_date || "N/A",
        "Last Payment Date": loan.last_payment_date || "No payments yet",
        "Paid Amount (K)": loan.paid_amount || 0,
        "Payment Progress (%)":
          Math.round((loan.paid_amount / loan.amount) * 100) || 0,
        "Collateral Items": loan.collateral?.length || 0,
        "GPS Trackers": loan.gps_trackers?.length || 0,
        "Is Commodity Loan": loan.is_commodity_loan ? "Yes" : "No",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 5 }, // S/N
        { wch: 25 }, // Borrower Name
        { wch: 20 }, // Loan Product
        { wch: 15 }, // Amount
        { wch: 15 }, // Status
        { wch: 20 }, // Loan Officer
        { wch: 15 }, // Officer Role
        { wch: 15 }, // Application Date
        { wch: 15 }, // Approval Date
        { wch: 18 }, // Next Payment Date
        { wch: 18 }, // Last Payment Date
        { wch: 15 }, // Paid Amount
        { wch: 18 }, // Payment Progress
        { wch: 15 }, // Collateral Items
        { wch: 15 }, // GPS Trackers
        { wch: 18 }, // Is Commodity Loan
      ];
      worksheet["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "All_Loans");

      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `All_Loans_${currentDate}.xlsx`;

      XLSX.writeFile(workbook, filename);

      message.success(
        `Successfully exported ${filteredLoans.length} loans to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export loans to Excel");
    }
  };

  // Calculate summary statistics using useMemo to ensure proper re-rendering
  const summaryStats = useMemo(() => {
    const totalAmount = filteredLoans.reduce(
      (sum, loan) => sum + loan.amount,
      0
    );
    const totalPaid = filteredLoans.reduce(
      (sum, loan) => sum + (loan.paid_amount || 0),
      0
    );
    const activeLoanCount = filteredLoans.filter(
      (loan) => loan.loan_status === 7
    ).length;
    const overdueLoanCount = filteredLoans.filter(
      (loan) => loan.loan_status === 8
    ).length;

    return {
      totalLoans: filteredLoans.length,
      totalAmount,
      totalPaid,
      activeLoanCount,
      overdueLoanCount
    };
  }, [filteredLoans]);

  const getSummaryStats = () => {
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{ borderTop: `2px solid ${zambianColors.green}` }}
          >
            <Statistic
              title="Total Loans"
              value={summaryStats.totalLoans}
              valueStyle={{ color: zambianColors.green, fontSize: 12 }}
              prefix={
                <ProfileOutlined
                  style={{ fontSize: 16, color: zambianColors.green }}
                />
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{ borderTop: `2px solid ${zambianColors.orange}` }}
          >
            <Statistic
              title="Total Portfolio Value"
              value={`K ${summaryStats.totalAmount.toLocaleString()}`}
              valueStyle={{ color: zambianColors.orange, fontSize: 12 }}
              prefix={
                <DollarCircleOutlined
                  style={{ fontSize: 16, color: zambianColors.orange }}
                />
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderTop: `2px solid #52c41a` }}>
            <Statistic
              title="Total Paid"
              value={`K ${summaryStats.totalPaid.toLocaleString()}`}
              valueStyle={{ color: "#52c41a", fontSize: 12 }}
              prefix={
                <CheckCircleOutlined style={{ fontSize: 16, color: "green" }} />
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{ borderTop: `2px solid ${zambianColors.red}` }}
          >
            <Statistic
              title="Risk Metrics"
              value={`${summaryStats.activeLoanCount} Active / ${summaryStats.overdueLoanCount} Overdue`}
              valueStyle={{ fontSize: 12, color: zambianColors.red }}
              prefix={<AlertOutlined style={{ fontSize: 16, color: "red" }} />}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      title: "Borrower",
      dataIndex: "borrower",
      key: "borrower",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            {record.borrower ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text
                  strong
                  onClick={() => navigate(`/borrower/${record.borrower.id}`)}
                  style={{ color: zambianColors.green, cursor: "pointer" }}
                >
                  {`${record.borrower.first_name} ${record.borrower.last_name}`}
                </Text>
                {record.gps_trackers && record.gps_trackers.length > 0 && (
                  <Tooltip title="GPS Tracker">
                    <EnvironmentOutlined
                      style={{
                        color: "#52c41a",
                        fontSize: "16px",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            ) : (
              <Text strong style={{ color: zambianColors.red }}>
                No borrower assigned
              </Text>
            )}
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <Text strong style={{ color: zambianColors.black }}>
          K {amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "loan_status",
      key: "status",
      render: (status) => (
        <Tag
          icon={statusMap[status]?.icon}
          color={statusMap[status]?.color}
          style={{
            borderRadius: "5px",
            padding: status === 10 ? "4px 12px" : "0 10px",
            minWidth: status === 10 ? "110px" : "auto",
            textAlign: "center",
          }}
        >
          {statusMap[status]?.text}
        </Tag>
      ),
      width: 130,
    },
    {
      title: "Loan Officer",
      dataIndex: "assigned_to",
      key: "assigned_to",
      render: (officer) =>
        officer ? (
          <div>
            <Text strong>
              {officer.first_name} {officer.last_name}
            </Text>
            {officer.role?.name && (
              <div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {officer.role.name}
                </Text>
              </div>
            )}
          </div>
        ) : (
          <Text type="secondary">Unassigned</Text>
        ),
      width: 150,
    },
    {
      title: "Progress",
      dataIndex: "paid_amount",
      key: "progress",
      render: (paid_amount, record) => (
        <Progress
          percent={
            record.loan_status === 10
              ? 100
              : Math.round((paid_amount / record.amount) * 100) || 0
          }
          status={
            record.loan_status === 8
              ? "exception"
              : record.loan_status === 10
                ? "success"
                : "active"
          }
          strokeColor={
            record.loan_status === 8
              ? zambianColors.red
              : record.loan_status === 10
                ? "#52c41a"
                : zambianColors.green
          }
          size="small"
          style={{ width: "120px" }}
        />
      ),
      width: 140,
    },
    {
      title: "Due Date",
      dataIndex: "next_payment_date",
      key: "next_payment_date",
      render: (date, record) => {
        if (record.loan_status === 10) {
          return <Text type="secondary">Settled</Text>;
        }
        if (!date) {
          return <Text type="secondary">N/A</Text>;
        }

        const isToday = moment(date).isSame(moment(), "day");
        const isOverdue = moment(date).isBefore(moment(), "day");

        return (
          <Text
            style={{
              color: isToday ? "#ff4d4f" : isOverdue ? "#fa8c16" : "inherit",
              fontWeight: isToday ? "bold" : "normal",
            }}
          >
            {moment(date).format("DD MMM YYYY")}
            {isToday && (
              <div style={{ fontSize: "10px", color: "#ff4d4f" }}>
                DUE TODAY
              </div>
            )}
          </Text>
        );
      },
      sorter: (a, b) => {
        const aDate = a.next_payment_date
          ? moment(a.next_payment_date).unix()
          : 0;
        const bDate = b.next_payment_date
          ? moment(b.next_payment_date).unix()
          : 0;
        return aDate - bDate;
      },
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleViewDetails(record)}
            style={{
              backgroundColor: zambianColors.lightGreen,
              borderColor: zambianColors.green,
            }}
          >
            Details
          </Button>
          {user?.role === "Administrator" &&
            (!record.assigned_to || !record.assigned_to.id) && (
              <Button
                type="default"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => handleReassignLoan(record)}
                style={{
                  backgroundColor: zambianColors.orange,
                  borderColor: zambianColors.orange,
                  color: "white",
                }}
              >
                Assign
              </Button>
            )}
        </Space>
      ),
      width: 150,
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <LineWave
          visible={true}
          height="100"
          width="100"
          color={zambianColors.green}
          ariaLabel="line-wave-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: zambianColors.red }}>
              Error loading loans: {error.message}
            </span>
          }
        />
        <Button
          type="primary"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 16,
            backgroundColor: zambianColors.green,
            borderColor: zambianColors.green,
          }}
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="all-loans-page" style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title
            level={3}
            style={{
              margin: 0,
              color: zambianColors.green,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ProfileOutlined style={{ marginRight: 8 }} />
            Loans Disbursed
          </Title>
          <Text type="secondary">
            Showing {filteredLoans.length} of {loans.length} loans
          </Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Export to Excel">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportToExcel}
                style={{
                  backgroundColor: zambianColors.green,
                  borderColor: zambianColors.green,
                  color: "white",
                }}
              >
                Export
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={6}>
            <Input
              placeholder="Search by borrower name or loan ID"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={5}>
            <Select
              placeholder="Filter by Loan Officer"
              value={selectedOfficer}
              onChange={setSelectedOfficer}
              allowClear
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              <Select.Option key="unassigned" value="unassigned">
                <Text style={{ color: zambianColors.red }}>
                  Unassigned Loans
                </Text>
              </Select.Option>
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                  {user.role?.name && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({user.role.name})
                    </Text>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Filter by Status"
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
              style={{ width: "100%" }}
            >
              {Object.entries(statusMap).map(([key, status]) => (
                <Select.Option key={key} value={parseInt(key)}>
                  {status.text}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
              placeholder={["Payment Due", "Payment Due"]}
            />
          </Col>
          <Col xs={24} sm={3}>
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              style={{ width: "100%" }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      <div className="all-loans-summary-cards">
        {getSummaryStats()}
      </div>

      <Card
        title="Loans Disbursed"
        bordered={false}
        style={{
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "8px",
        }}
        headStyle={{
          backgroundColor: "#f0f8f0",
          color: zambianColors.green,
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
        }}
      >
        <Table
          columns={columns}
          dataSource={filteredLoans}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} loans`,
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Reassignment Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: zambianColors.green,
            }}
          >
            <UserAddOutlined style={{ marginRight: 8 }} />
            Assign Loan Officer
          </div>
        }
        open={showReassignModal}
        onCancel={() => {
          setShowReassignModal(false);
          reassignForm.resetFields();
        }}
        onOk={() => reassignForm.submit()}
        okButtonProps={{
          style: {
            backgroundColor: zambianColors.green,
            borderColor: zambianColors.green,
          },
        }}
      >
        <Form
          form={reassignForm}
          onFinish={handleReassignSubmit}
          layout="vertical"
        >
          {selectedLoanForReassign && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Text strong>Loan Details:</Text>
              <div>
                <Text>Loan ID: #{selectedLoanForReassign.id}</Text>
              </div>
              <div>
                <Text>
                  Borrower:{" "}
                  {selectedLoanForReassign.borrower
                    ? `${selectedLoanForReassign.borrower.first_name} ${selectedLoanForReassign.borrower.last_name}`
                    : "No borrower assigned"}
                </Text>
              </div>
              <div>
                <Text>
                  Amount: K {selectedLoanForReassign.amount?.toLocaleString()}
                </Text>
              </div>
            </div>
          )}

          <Form.Item
            label="Assign to Loan Officer"
            name="assigned_to"
            rules={[
              { required: true, message: "Please select a loan officer" },
            ]}
          >
            <Select
              placeholder="Select loan officer"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                  {user.role?.name && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({user.role.name})
                    </Text>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LoansDisbursedPage;
